/**
 * Palette de la plateforme : des couleurs de BASE réglées par l'exploitant
 * (périmètre superuser), et les jetons CSS qui en DÉCOULENT.
 *
 * Fonctions PURES : ni DOM, ni réseau, ni stockage — tout ce qui décide d'une
 * couleur se teste ici. L'application au document vit dans `appliquer.ts`.
 *
 * ─── Pourquoi une couleur de base et pas chaque jeton ───
 *
 * `--color-primary` a six déclinaisons (survol, pressé, fond doux, bordure,
 * texte, composantes RVB) et quatre jetons composés (dégradé, halo, anneau de
 * focus). Les faire saisir une à une, c'est garantir qu'au troisième réglage
 * le survol d'un bouton n'a plus rien à voir avec le bouton. On saisit UNE
 * couleur par famille et par thème ; le reste se calcule, toujours de la même
 * façon.
 *
 * ─── Ce qui n'est PAS surchargé ───
 *
 * Le contraste renforcé (`[data-contrast='high']`, `assets/main.css`) garde la
 * main : c'est un choix d'accessibilité de l'utilisateur, et sa palette est
 * vérifiée pour le contraste — une palette de marque ne l'est pas. Les sélecteurs
 * de la feuille générée l'excluent donc explicitement.
 */

/**
 * Couleurs réglables. MIROIR de `COULEURS_REGLABLES` (backend
 * `core/apparence/schemas.ts`) et de la fonction de validation de la migration
 * 0044 : une couleur ajoutée d'un seul côté serait refusée par le serveur, ou
 * acceptée sans rien changer à l'écran.
 */
export const COULEURS_REGLABLES = [
  'primary',
  'accent',
  'success',
  'warning',
  'danger',
  'info',
] as const
export type CouleurReglable = (typeof COULEURS_REGLABLES)[number]

export const THEMES = ['light', 'dark'] as const
export type Theme = (typeof THEMES)[number]

export type Surcharges = Partial<Record<CouleurReglable, string>>
export interface CouleursPlateforme {
  light: Surcharges
  dark: Surcharges
}

/**
 * Valeurs de base de `assets/main.css` (couche ADS, la dernière déclarée), par
 * thème. Répétées ici parce que l'écran d'édition doit montrer la couleur EN
 * VIGUEUR d'une famille non surchargée, et qu'un `getComputedStyle` lirait la
 * surcharge courante, pas le défaut. Un test relit `main.css` et échoue si les
 * deux divergent.
 */
export const DEFAUTS: Readonly<Record<Theme, Readonly<Record<CouleurReglable, string>>>> = {
  light: {
    // Turquoise de la marque Premium Energy (`main.css`, couche ADS).
    primary: '#49e6e0',
    accent: '#6e5dc6',
    success: '#22a06b',
    warning: '#e2b203',
    danger: '#c9372c',
    info: '#1d7afc',
  },
  dark: {
    primary: '#49e6e0',
    accent: '#9f8fef',
    success: '#2abb7f',
    warning: '#cf9f02',
    danger: '#f15b50',
    info: '#579dff',
  },
}

/**
 * Neutres de chaque thème (même source, même test) : le fond sert au calcul du
 * contraste et des fonds doux sombres ; l'ensemble sert à l'APERÇU, qui doit
 * montrer le thème clair même quand l'écran est en sombre.
 */
export const NEUTRES: Readonly<
  Record<Theme, Readonly<{ bg: string; surface: string; text: string; textMuted: string; border: string }>>
> = {
  light: {
    bg: '#ffffff',
    surface: '#ffffff',
    text: '#172b4d',
    textMuted: '#44546f',
    border: '#dcdfe4',
  },
  dark: {
    bg: '#1d2125',
    surface: '#22272b',
    text: '#b6c2cf',
    textMuted: '#9fadbc',
    border: '#38414a',
  },
}

/**
 * Seuil de contraste signalé à l'écran : 3:1, le minimum WCAG pour un élément
 * graphique ou un texte de grande taille sur le fond. En dessous, un bouton ou
 * une pastille de cette couleur se distingue mal de la page.
 */
export const CONTRASTE_MINIMUM = 3

const HEX = /^#[0-9a-f]{6}$/

/**
 * `#rrggbb` en minuscules, et RIEN d'autre. C'est la seule forme qui entre dans
 * la feuille de style générée : toute syntaxe plus large (nom CSS, `rgb()`,
 * point-virgule) ouvrirait une injection CSS. Même règle que le serveur, revérifiée
 * ici parce que la valeur peut aussi venir du stockage local.
 */
export function estCouleur(valeur: unknown): valeur is string {
  return typeof valeur === 'string' && HEX.test(valeur)
}

/**
 * Valide une forme reçue (réponse d'API, cache local) et la renvoie copiée, ou
 * `null` si UN seul élément est hors contrat. Tout ou rien : appliquer la moitié
 * d'une palette suspecte donnerait un rendu que personne n'a choisi.
 */
export function normaliser(brut: unknown): CouleursPlateforme | null {
  if (typeof brut !== 'object' || brut === null || Array.isArray(brut)) return null
  const resultat: CouleursPlateforme = { light: {}, dark: {} }
  for (const [theme, surcharges] of Object.entries(brut)) {
    if (!(THEMES as readonly string[]).includes(theme)) return null
    if (typeof surcharges !== 'object' || surcharges === null || Array.isArray(surcharges)) {
      return null
    }
    for (const [couleur, valeur] of Object.entries(surcharges)) {
      if (!(COULEURS_REGLABLES as readonly string[]).includes(couleur)) return null
      if (!estCouleur(valeur)) return null
      resultat[theme as Theme][couleur as CouleurReglable] = valeur
    }
  }
  return resultat
}

// ---------------------------------------------------------------------------
// Arithmétique des couleurs
// ---------------------------------------------------------------------------

type Rvb = readonly [number, number, number]

function versRvb(hex: string): Rvb {
  if (!estCouleur(hex)) throw new Error(`Couleur hors contrat : ${JSON.stringify(hex)}`)
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ]
}

function versHex([r, g, b]: Rvb): string {
  return `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`
}

/** Mélange linéaire en sRVB : `part` de `vers` dans `depuis` (0 = `depuis`). */
export function melanger(depuis: string, vers: string, part: number): string {
  const a = versRvb(depuis)
  const b = versRvb(vers)
  return versHex([0, 1, 2].map((i) => a[i]! + (b[i]! - a[i]!) * part) as unknown as Rvb)
}

/** Luminance relative WCAG 2.x. */
function luminance(hex: string): number {
  const lineaire = versRvb(hex).map((c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * lineaire[0]! + 0.7152 * lineaire[1]! + 0.0722 * lineaire[2]!
}

/** Rapport de contraste WCAG entre deux couleurs, de 1 à 21. */
export function contraste(a: string, b: string): number {
  const [claire, sombre] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number]
  return (claire + 0.05) / (sombre + 0.05)
}

// ---------------------------------------------------------------------------
// Dérivation des jetons
// ---------------------------------------------------------------------------

const BLANC = '#ffffff'
const NOIR = '#000000'

function composantes(hex: string): string {
  return versRvb(hex).join(' ')
}

function rgba(hex: string, alpha: number): string {
  return `rgba(${versRvb(hex).join(', ')}, ${alpha})`
}

/**
 * Fond doux, bordure et texte d'une famille d'état — la même règle pour toutes,
 * c'est ce qui garde les pastilles succès/danger/info cohérentes entre elles.
 *
 * Fonds doux OPAQUES en sombre, comme la couche ADS : un alpha changerait de
 * teinte posé sur une surface déjà teintée (ligne sélectionnée, survol).
 */
function etat(theme: Theme, base: string): { soft: string; border: string; text: string } {
  if (theme === 'light') {
    return {
      soft: melanger(base, BLANC, 0.9),
      border: melanger(base, BLANC, 0.5),
      text: melanger(base, NOIR, 0.25),
    }
  }
  return {
    soft: melanger(base, NEUTRES.dark.bg, 0.75),
    border: melanger(base, NOIR, 0.3),
    text: melanger(base, BLANC, 0.35),
  }
}

/**
 * Jetons CSS dérivés d'UNE couleur de base, pour un thème. Les noms sont ceux
 * de `assets/main.css` ; aucun nouveau jeton n'est inventé — les composants ne
 * savent pas que la palette est réglable.
 */
export function jetonsDerives(
  theme: Theme,
  couleur: CouleurReglable,
  base: string,
): Record<string, string> {
  const clair = theme === 'light'
  switch (couleur) {
    case 'primary': {
      const e = etat(theme, base)
      return {
        '--color-primary': base,
        '--color-primary-hover': clair ? melanger(base, NOIR, 0.15) : melanger(base, BLANC, 0.25),
        '--color-primary-active': clair ? melanger(base, NOIR, 0.45) : melanger(base, BLANC, 0.55),
        '--color-primary-soft': e.soft,
        '--color-primary-border': e.border,
        '--color-primary-text': e.text,
        '--color-primary-rgb': composantes(base),
        // Aplats, comme la couche ADS : le jeton survit parce que des
        // composants du socle l'emploient.
        '--gradient-brand': `linear-gradient(0deg, ${base}, ${base})`,
        '--gradient-brand-soft': `linear-gradient(0deg, ${e.soft}, ${e.soft})`,
        '--shadow-glow': `0 0 0 2px ${rgba(base, clair ? 0.3 : 0.4)}`,
        '--ring-primary': `0 0 0 2px var(--color-bg), 0 0 0 4px ${
          clair ? melanger(base, BLANC, 0.2) : melanger(base, BLANC, 0.25)
        }`,
      }
    }
    case 'accent':
      return { '--color-accent': base, '--color-accent-rgb': composantes(base) }
    case 'warning': {
      // Deux familles jumelles dans `main.css` (`warn`, historique, et
      // `warning`) : les composants emploient l'une ou l'autre, une seule
      // couleur réglée doit donc piloter les deux.
      const e = etat(theme, base)
      const jetons: Record<string, string> = {}
      for (const prefixe of ['--color-warn', '--color-warning']) {
        jetons[prefixe] = base
        jetons[`${prefixe}-soft`] = e.soft
        jetons[`${prefixe}-border`] = e.border
        jetons[`${prefixe}-text`] = e.text
      }
      return jetons
    }
    case 'success':
    case 'danger':
    case 'info': {
      const e = etat(theme, base)
      const jetons: Record<string, string> = {
        [`--color-${couleur}`]: base,
        [`--color-${couleur}-soft`]: e.soft,
        [`--color-${couleur}-border`]: e.border,
        [`--color-${couleur}-text`]: e.text,
      }
      if (couleur === 'danger') {
        jetons['--ring-danger'] = `0 0 0 2px var(--color-bg), 0 0 0 4px ${base}`
      }
      return jetons
    }
  }
}

/** Tous les jetons dérivés des SURCHARGES d'un thème (rien pour une famille non réglée). */
export function jetonsDuTheme(theme: Theme, surcharges: Surcharges): Record<string, string> {
  const jetons: Record<string, string> = {}
  for (const couleur of COULEURS_REGLABLES) {
    const base = surcharges[couleur]
    if (base !== undefined) Object.assign(jetons, jetonsDerives(theme, couleur, base))
  }
  return jetons
}

/**
 * Sélecteurs de la feuille générée. Spécificité (0,3,0) pour les deux : elle
 * l'emporte sur les deux couches de `main.css` (`:root`, `[data-theme='dark']`,
 * spécificité (0,1,0)) QUEL QUE SOIT l'ordre d'insertion des feuilles — en
 * développement, Vite peut injecter la sienne après la nôtre. Le contraste
 * renforcé est exclu : voir l'en-tête.
 */
export const SELECTEURS: Readonly<Record<Theme, string>> = {
  light: ":root:not([data-theme='dark']):not([data-contrast='high'])",
  dark: ":root[data-theme='dark']:not([data-contrast='high'])",
}

/**
 * Feuille de style complète des surcharges, `''` s'il n'y en a aucune.
 *
 * Lève sur une valeur hors contrat, au lieu de l'ignorer : on n'arrive ici
 * qu'après `normaliser`, et une valeur invalide à ce stade est un bug qu'il faut
 * voir — pas une couleur à deviner.
 */
export function feuilleDeStyle(couleurs: CouleursPlateforme): string {
  const blocs: string[] = []
  for (const theme of THEMES) {
    const jetons = jetonsDuTheme(theme, couleurs[theme])
    const lignes = Object.entries(jetons).map(([nom, valeur]) => `  ${nom}: ${valeur};`)
    if (lignes.length > 0) blocs.push(`${SELECTEURS[theme]} {\n${lignes.join('\n')}\n}`)
  }
  return blocs.join('\n')
}
